import './form-comments.scss';

import { Collapse, notification } from 'antd';
import React, { useEffect, useState } from 'react';

import { ieLeasingApi } from '@/components/App/Leasing/api';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import Chevron from '@/media/icons/chevron.svg?react';
import { handleError } from '@/services/handleError';
import { getUserFullName } from '@/services/helpers/strings';
import { useAppSelector } from '@/store';

import { Button, TextArea } from '../../ui';
import type {
  FormCommentRequest,
  FormCommentsDto
} from './types/form-comments-type';

type TProps = {
  type: 'auto-credit' | 'ie-leasing';
  isAdaptive?: boolean;
};

const maxLength = 250;

const FormComments: React.FC<TProps> = ({ type, isAdaptive = false }) => {
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { request: ieLeasingRequest } = useAppSelector(
    (state) => state.ieLeasingOrder
  );

  const { userInfo } = useAppSelector((state) => state.userInfo);

  const [commentItem, setCommentItem] = useState<FormCommentRequest | null>(
    null
  );
  const [commentMessage, setCommentMessage] = useState('');

  useEffect(() => {
    if (type === 'auto-credit' && !orderApp) return;
    if (type === 'ie-leasing' && !ieLeasingRequest) return;

    const comments =
      type === 'auto-credit'
        ? orderApp?.Requests_Comments || []
        : ieLeasingRequest?.comments || [];

    const latestComment =
      comments.length > 0 ? comments[comments.length - 1] : null;

    if (latestComment) {
      setCommentItem(latestComment);
      setCommentMessage(latestComment.comment);
    }
  }, [orderApp, ieLeasingRequest, type]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= maxLength) {
      setCommentMessage(e.target.value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requestUuid =
      type === 'auto-credit' ? orderApp?.uuid : ieLeasingRequest?.uuid;

    if (!requestUuid || !userInfo) return;

    const body: FormCommentsDto = {
      comment: commentMessage,
      uuid: requestUuid
    };

    try {
      if (commentItem?.uuid) {
        const response =
          type === 'auto-credit'
            ? await autoCreditApi.updateComment(commentItem.uuid, body)
            : await ieLeasingApi.updateComment(commentItem.uuid, body);

        setCommentItem({
          ...commentItem,
          comment: response.comment,
          update_date: response.update_date,
          update_user: userInfo
        });

        setCommentMessage(response.comment);

        notification.success({
          title: 'Комментарий обновлён',
          description: 'Комментарий успешно изменён',
          duration: 5
        });
      } else {
        if (!commentMessage.trim()) return;

        if (type === 'auto-credit') {
          await autoCreditApi.createComment(body, requestUuid);
        } else {
          await ieLeasingApi.createComment(body, requestUuid);
        }
        notification.success({
          title: 'Комментарий создан',
          description: 'Комментарий успешно добавлен к заявке',
          duration: 5
        });
      }
    } catch (error) {
      handleError(error, {
        customTitle: 'Не удалось сохранить комментарий'
      });
    }
  };

  if (isAdaptive) {
    return (
      <div className="comment-container__collapse-container">
        <Collapse
          expandIconPlacement="end"
          className="comment-container__collapse"
          expandIcon={({ isActive }) => (
            <span
              className={`comment-container__collapse-arrow ${isActive ? 'comment-container__collapse-arrow--rotated' : ''}`}
            >
              <Chevron />
            </span>
          )}
          items={[
            {
              label: (
                <span className="comment-container__collapse-label">
                  Комментарий по клиенту{' '}
                  {!!commentItem && (
                    <span className="comment-container__header-user">
                      От{' '}
                      {getUserFullName(
                        commentItem.update_user || commentItem.create_user,
                        {
                          pattern: 'first_last'
                        }
                      )}
                    </span>
                  )}
                </span>
              ),
              children: (
                <form
                  className="comment-container__form"
                  onSubmit={handleSubmit}
                >
                  <TextArea
                    className="comment-container__input comment-container__input--adaptive"
                    value={commentMessage}
                    onChange={handleChange}
                    placeholder="Введите комментарий (до 250 символов)"
                    maxLength={maxLength}
                    rows={4}
                  />

                  <div className="comment-container__info">
                    {commentItem && (
                      <div className="comment-container__meta">
                        <span>
                          {getUserFullName(
                            commentItem.update_user || commentItem.create_user,
                            {
                              pattern: 'first_last'
                            }
                          )}
                        </span>
                        <span>
                          {new Date(
                            commentItem.update_date || commentItem.create_date
                          ).toLocaleString()}
                        </span>
                      </div>
                    )}

                    <span className="comment-container__char-count">
                      {commentMessage.length}/{maxLength}
                    </span>
                  </div>

                  <Button
                    variant="filled-red"
                    size="small"
                    type="submit"
                    className="comment-container__button"
                    disabled={commentItem ? false : !commentMessage.trim()}
                  >
                    {commentItem ? 'Изменить' : 'Отправить'}
                  </Button>
                </form>
              )
            }
          ]}
        />
      </div>
    );
  }
  return (
    <div className="comment-container">
      <form className="comment-container__form" onSubmit={handleSubmit}>
        <p className="comment-container__header">Комментарий по клиенту</p>

        <TextArea
          className="comment-container__input"
          value={commentMessage}
          onChange={handleChange}
          placeholder="Введите комментарий (до 250 символов)"
          maxLength={maxLength}
          rows={4}
        />

        <div className="comment-container__info">
          {commentItem && (
            <div className="comment-container__meta">
              <span>
                {getUserFullName(
                  commentItem.update_user || commentItem.create_user,
                  {
                    pattern: 'first_last'
                  }
                )}
              </span>
              <span>
                {new Date(
                  commentItem.update_date || commentItem.create_date
                ).toLocaleString()}
              </span>
            </div>
          )}

          <span className="comment-container__char-count">
            {commentMessage.length}/{maxLength}
          </span>
        </div>

        <Button
          variant="filled-red"
          size="small"
          type="submit"
          className="comment-container__button"
          disabled={commentItem ? false : !commentMessage.trim()}
        >
          {commentItem ? 'Изменить' : 'Отправить'}
        </Button>
      </form>
    </div>
  );
};

export default FormComments;
